
import AppRow from "./SaleEditDesktopTableRow.vue";
import AppSaleRowEditDeliveryDateModal from "./SaleRowEditDeliveryDateModal.vue";
import AppSaleEditRowImage from "./SaleEditRowImage.vue";
import AppContextMenu from "@/components/browse/ContextMenu.vue";
import { copyToClipboard } from "@/utils/clipboard";
import Vue from "vue";
import { SaleEditState } from "@/utils/sale-edit/sale-edit-state";
import { SaleRow } from "@/models/base";
import {DossierConfigService, UserConfigService} from "@/services";
import {ISelectItem} from "winfakt-vue-components";
import { TranslateResult } from "vue-i18n";
import { SaleConfigs } from "@/models/dossier-configs/sale-configs";
import {AWSFile} from "@/models/aws-file";

export default Vue.extend({
	data(){
		return {
			scrollOffset: 0,
			renderedRowsStart: 0,
			renderedRowsEnd: 0,
			renderLoop: 0,
			shouldRecomputeRenderedRows: false,
			compact: UserConfigService.getSaleSettings().CompactSaleOverview,
			full: UserConfigService.getSaleSettings().FullSaleRowView,
			DossierConfigService,
			UserConfigService,
			editDeliveryDate: false,
			cellContextMenu: {
				opened: false,
				mouseX: 0,
				mouseY: 0,
				text: "",
			},
		};
	},
	props: {
		state: {
			type: Object as ()=>SaleEditState,
			required: true,
		}
	},
	computed: {
		cellContextMenuItems(): ISelectItem<Function>[] {
			return [{
				text: this.$t("components.browse.copy-field-to-clipboard").toString(),
				icon: "copy",
				value: this.copyCellText
			}];
		},
		options(): ISelectItem<Function>[] {
			let data:ISelectItem<Function>[] = [
				{text: ( this.$ct("default").toString()), value: this.toggleDefault, icon: !this.compact&&!this.full ? "check" : ""},
				{text: (this.$ct("compact").toString()), value: this.toggleCompact, icon: this.compact ? "check" : ""},
				{text: (this.$ct("full").toString()), value: this.toggleFull, icon: this.full ? "check" : ""},
			];
			return data;
		},
		saleConfigs():SaleConfigs{
			return this.DossierConfigService.getSaleConfigs();
		},
		showQty2():boolean {
			return this.saleConfigs.UseQty2;
		},
		qtyLabel():TranslateResult{
			if (!this.showQty2) {
				return this.$ct("sales-row.Qty");
			}
			return this.$ct("sales-row.Qty1");
		},
		qty2Label():TranslateResult | string{
			if (this.saleConfigs.Qty2Label){
				return this.saleConfigs.Qty2Label;
			}
			return this.$ct("sales-row.Qty2");
		},
		unitLabel(): string | TranslateResult {
			let name = DossierConfigService.getGeneralConfig().UnitFieldName;
			if (name) {
				return name;
			}
			return this.$ct("sales-row.Unit");
		},
		rows():SaleRow[]{
			return this.state.sale.Rows;
		},
		selectedRow():SaleRow | null{
			return this.state.selectedRow;
		},
		sortableAttributes():any {
			return{
				tag: "tbody",
				disableX: true,
				keepInY: true,
				targetClass: "handle",
				items: this.rows,
				indexOffset: this.renderedRowsStart
			};
		},
		renderedRowsPadding():number {
			if (this.rows.length <= 100) {
				return 100;
			}
			return 10;
		},
		renderedRowsDiffTillRecompute():number{
			return 5;
		},
		rowHeight():number{
			return 46;
		},
		showUnits():Boolean {
			return this.UserConfigService.getSaleSettings().ShowUnits;
		},
		widthWithoutDescription(): number{
			let total = 804;
			if (this.showUnits){
				total += 80;
			}
			if (this.showQty2){
				total += 120;
			}
			if (this.state.isPurchaseType){
				total += 125;
			}
			total += 32; //image icon
			return total;
		},
		scrollOffsetBottom():number {
			if (!this.$refs.scrollView) return this.scrollOffset + 1000;
			let scrollView = this.$refs.scrollView as any;
			return this.scrollOffset +  (scrollView.$el as HTMLElement).clientHeight;
		},
		beforeHeight():number {
			let count = this.renderedRowsStart;
			return this.rowHeight * count;
		},
		afterHeight():number {
			let end = this.renderedRowsEnd;
			let count = this.rows.length - end;
			return this.rowHeight * count;
		},
		renderedRows():SaleRow[]{
			let begin = this.renderedRowsStart;
			let end = this.renderedRowsEnd;
			return this.rows.slice(begin, end);
		},
		colCount():number{
			let result = 9;
			if (this.showUnits) {
				result++;
			}
			return result;
		}
	},
	watch: {
		rows: {
			handler(){
				this.computeRenderedRowsOffset();
			}
		}
	},
	methods: {
		onCellContextMenu(event:MouseEvent){
			let target = event.target as HTMLElement | null;
			if (!target || !target.closest) return;
			let cell = target.closest("td");
			if (!cell) return;
			let text = cell.innerText.trim();
			if (!text) return;
			event.preventDefault();
			this.cellContextMenu.text = text;
			this.cellContextMenu.mouseX = event.pageX;
			this.cellContextMenu.mouseY = event.pageY;
			this.cellContextMenu.opened = true;
		},
		onCellContextMenuClick(value:Function){
			this.cellContextMenu.opened = false;
			value();
		},
		copyCellText(){
			if (!this.cellContextMenu.text) return;
			copyToClipboard(this.cellContextMenu.text);
			this.$wf.notify(this.$t("components.browse.copied-to-clipboard").toString(), "success", 3000, "check");
		},
		onFileInput(file:AWSFile){
			this.state.selectedRow.CoverImageOverrideFileID = file.ID;
			this.state.selectedRow.CoverImageOverrideFile=file;
		},
		onClearImage(){
			this.state.selectedRow.CoverImageOverrideFileID = 0;
			this.state.selectedRow.CoverImageOverrideFile = null;
		},
		async onShowImageUpload(row:SaleRow){
			this.state.showImageUploadModal=true;
			await this.$nextTick();
			setTimeout(() => {
				this.state.selectedRow = row;
			}, 200);
		},
		async editDeliver(row:SaleRow){
			this.editDeliveryDate=true;
			await this.$nextTick();
			setTimeout(() => {
				this.state.selectedRow = row;
			}, 200);
		},
		toggleDefault(){
			this.compact = false;
			this.full = false;
			let settings = UserConfigService.getSaleSettings();
			settings.CompactSaleOverview = false;
			settings.FullSaleRowView = false;
			UserConfigService.setSaleSettings(settings);
		},
		toggleCompact(){
			this.compact = true;
			this.full = false;
			let settings = UserConfigService.getSaleSettings();
			settings.CompactSaleOverview = true;
			settings.FullSaleRowView = false;
			UserConfigService.setSaleSettings(settings);
		},
		toggleFull(){
			this.compact = false;
			this.full = true;
			let settings = UserConfigService.getSaleSettings();
			settings.CompactSaleOverview = false;
			settings.FullSaleRowView = true;
			UserConfigService.setSaleSettings(settings);
		},
		onScroll(event:Event){
			this.scrollOffset = (event.target as HTMLDivElement).scrollTop;
			this.shouldRecomputeRenderedRows = true;
		},
		renderLoopItteration(){
			if (!this.shouldRecomputeRenderedRows) return;
			this.checkIfShouldRecomputeOffset();
			this.shouldRecomputeRenderedRows = false;
		},
		checkIfShouldRecomputeOffset(){
			let begin = Math.floor(this.scrollOffset / this.rowHeight) - this.renderedRowsPadding + this.renderedRowsDiffTillRecompute;
			if (begin < 0){
				begin = 0;
			}
			if (begin < this.renderedRowsStart){
				return this.computeRenderedRowsOffset();
			}
			let end = Math.ceil(this.scrollOffsetBottom / this.rowHeight) + this.renderedRowsPadding + this.renderedRowsDiffTillRecompute;;
			if (end > this.rows.length){
				end = this.rows.length;
			}
			if (end > this.renderedRowsEnd){
				return this.computeRenderedRowsOffset();
			}
		},
		computeRenderedRowsOffset(){
			let begin = Math.floor(this.scrollOffset / this.rowHeight) - this.renderedRowsPadding;
			if (begin < 0){
				begin = 0;
			}
			let end = Math.ceil(this.scrollOffsetBottom / this.rowHeight) + this.renderedRowsPadding;
			if (end > this.rows.length){
				end = this.rows.length;
			}
			this.renderedRowsStart = begin;
			this.renderedRowsEnd = end;
		},
		/* eslint-disable-next-line max-lines-per-function */
		onKeyDown(e: KeyboardEvent) {
			if (e.keyCode == 115) {
				this.$emit("add-subtotal");
			}
			if (e.keyCode == 118) {
				this.state.mergeSaleRows();
			}

			if ((e.ctrlKey || e.metaKey) && (e.key === "d" || e.key === "D")) {
				e.preventDefault();
				this.state.rowSelection.duplicate();
				return;
			}

			if ((e.target as HTMLTextAreaElement).className.indexOf("w-input") >= 0 ||
				(e.target as HTMLTextAreaElement).className.indexOf("w-textarea") >= 0 ||
				(e.target as HTMLTextAreaElement).className.indexOf("ql-editor") >= 0 ||
				(e.target as HTMLTextAreaElement).className.indexOf("editor-area") >= 0 ||
				(e.target as HTMLElement).isContentEditable ||
				(e.target as HTMLTextAreaElement).className.indexOf("w-select-button") >= 0){
				return;
			}

			let m = document.getElementsByClassName("modal");
			if (m[0] && m[0].querySelector(".modal") !== null) return;

			if (e.keyCode == 46 || e.keyCode == 8 ) {
				if (this.state.rowSelection.selectedRows.length > 0) {
					this.state.rowSelection.remove();
				} else {
					this.state.removeRow(this.state.selectedRow);
				}
			}

			if (e.keyCode == 32 ) {
				//toggle row visibility
				this.state.selectedRow.getRowConfig().Hidden = !this.state.selectedRow.getRowConfig().Hidden;
				this.state.sale.calculateTotals();
			}
			if (e.keyCode == 45 ) {
				//insert row above
				this.state.insertRowBefore();
			}
			if (e.keyCode == 40 ) {
				this.state.selectNextRow();
				let container = this.$el.querySelector("#products-table > tbody");
				if (container)
					container.scrollTop = (container.scrollTop + 38);
			}
			if (e.keyCode == 38 ) {
				this.state.selectPreviousRow();
				let container = this.$el.querySelector("#products-table > tbody");
				if (container)
					container.scrollTop = (container.scrollTop - 38);
			}
			/*if (e.keyCode == 13 ) {
				setTimeout(() => {
					e.preventDefault();
					this.state.productEditRowState.focusQty();
				},150);
			}*/
			if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "C")) {
				this.state.rowSelection.copy();
			}
			if ((e.ctrlKey || e.metaKey) && (e.key === "x" || e.key === "X")) {
				this.state.rowSelection.cut();
			}
			if ((e.ctrlKey || e.metaKey) && (e.key === "v" || e.key === "V")) {
				this.state.rowSelection.paste();
			}
			if (e.ctrlKey && e.key === "b") {
				this.state.toggleBold();
			}
			if (e.ctrlKey && e.key === "u") {
				this.state.toggleUnderline();
			}
			if (e.ctrlKey && e.key === "+") {
				this.state.insertRowBefore();
			}
			if (e.ctrlKey && e.key === "e") {
				this.state.toEditRowExtraFields = this.selectedRow;
			}
			if (e.ctrlKey && e.key === "p") {
				if(this.state.selectedRow !== new SaleRow()){
					e.preventDefault();
					this.state.showImageUploadModal = true;
				}
			}
			return;
		},
	},
	mounted(){
		this.computeRenderedRowsOffset();
		this.renderLoop = setInterval(this.renderLoopItteration.bind(this), 100);
	},
	destroyed(){
		clearInterval(this.renderLoop);
		window.removeEventListener("keydown", this.onKeyDown);
	},
	created() {
		window.addEventListener("keydown", this.onKeyDown);
	},
	components: {
		AppRow,
		AppSaleRowEditDeliveryDateModal,
		AppSaleEditRowImage,
		AppContextMenu
	},
	i18nEx: {
		componentPrefix: "components.products-table"
	}
});
